// src/pages/editor-tasks/UnassignedEditorTasks.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function UnassignedEditorTasks() {
  const [tasks, setTasks] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [message, setMessage] = useState('');
  const { currentTheme } = useContext(ThemeContext);
  const navigate = useNavigate();


  // Colors
  const colors = {
    backgroundDark: '#0f0c29',
    backgroundMedium: '#302b63',
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444'
  };

  useEffect(() => {
    fetchUnassignedTasks();
    fetchDesigners();
  }, []);

  const fetchUnassignedTasks = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      const response = await fetch(`${API_BASE}/get_unassigned_editor_tasks.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && Array.isArray(data.tasks)) {
        setTasks(data.tasks);
      } else {
        setTasks([]);
        setMessage('No unassigned tasks found');
      }
    } catch (error) {
      console.error('Error fetching unassigned tasks:', error);
      setMessage(`❌ Error: ${error.message}`);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDesigners = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_designers.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        setDesigners(data.data);
      } else {
        setDesigners([]);
      }
    } catch (error) {
      console.error('Error fetching designers:', error);
      setDesigners([]);
    }
  };

  const assignDesigner = async (taskId, designerId) => {
    if (!designerId) {
      setMessage('❌ Please select a designer');
      return;
    }

    try {
      setAssigning(true);
      setMessage('');

      const userId = sessionStorage.getItem('userId');
      const userName = sessionStorage.getItem('userName');

      const response = await fetch(`${API_BASE}/assign_designer_to_editor_task.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_id: taskId,
          designer_id: designerId,
          assigned_by: userId,
          assigned_by_name: userName
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage('✅ Designer assigned successfully!');
        fetchUnassignedTasks(); // Refresh tasks
      } else {
        setMessage(`❌ ${data.message || 'Error assigning designer'}`);
      }
    } catch (error) {
      console.error('Error assigning designer:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setAssigning(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === 'NULL') return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getVideoTypeBadge = (videoType) => {
    const types = {
      'indoor_reel': { color: '#6366F1', label: 'Indoor Reel', icon: 'fa-home' },
      'outdoor_reel': { color: '#10B981', label: 'Outdoor Reel', icon: 'fa-tree' },
      'podcast': { color: '#8B5CF6', label: 'Podcast', icon: 'fa-podcast' },
      'indoor_long_video': { color: '#F59E0B', label: 'Indoor Long', icon: 'fa-video' },
      'outdoor_long_video': { color: '#06B6D4', label: 'Outdoor Long', icon: 'fa-mountain' }
    };
    
    const type = types[videoType] || { color: '#6B7280', label: 'Unknown', icon: 'fa-question' };
    
    return (
      <span style={{
        background: type.color,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '8px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <i className={`fas ${type.icon}`} style={{ fontSize: '0.6rem' }}></i>
        {type.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: '#F59E0B', text: 'Pending' },
      'assigned': { bg: '#6366F1', text: 'Assigned' },
      'in_progress': { bg: '#06B6D4', text: 'In Progress' },
      'completed': { bg: '#10B981', text: 'Completed' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span style={{
        background: config.bg,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '8px',
        fontSize: '0.75rem',
        fontWeight: '600'
      }}>
        {config.text}
      </span>
    );
  };

  const openRawLink = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            color: colors.textLight,
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: `2px solid ${colors.primary}`,
              borderTop: `2px solid transparent`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Loading tasks...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
        padding: '20px'
      }}>
        {/* Header */}
        <div style={{
          marginBottom: '30px',
          padding: '20px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '15px',
          border: `1px solid ${colors.borderDark}`
        }}>
          <h1 style={{
            color: 'white',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <i className="fas fa-user-times"></i>
            Unassigned Editor Tasks
          </h1>
          <p style={{ color: colors.textLight }}>
            Assign designers to editor tasks without designer assignment
          </p>
          
          <div style={{
            display: 'flex',
            gap: '15px',
            marginTop: '15px',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={fetchUnassignedTasks}
              disabled={loading}
              style={{
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: loading ? 0.7 : 1
              }}
            >
              <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i>
              Refresh
            </button>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-tasks" style={{ color: colors.primary }}></i>
              <span style={{ color: colors.textLight }}>
                Tasks: <strong style={{ color: 'white' }}>{tasks.length}</strong>
              </span>
            </div>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-palette" style={{ color: colors.secondary }}></i>
              <span style={{ color: colors.textLight }}>
                Designers: <strong style={{ color: 'white' }}>{designers.length}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            borderRadius: '10px',
            background: message.includes('✅') 
              ? `rgba(16, 185, 129, 0.2)` 
              : message.includes('❌')
              ? `rgba(239, 68, 68, 0.2)`
              : `rgba(59, 130, 246, 0.2)`,
            border: `1px solid ${message.includes('✅') ? colors.success : message.includes('❌') ? colors.danger : colors.primary}`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : message.includes('❌') ? 'fa-exclamation-circle' : 'fa-info-circle'}`}
                 style={{ 
                   color: message.includes('✅') ? colors.success : 
                          message.includes('❌') ? colors.danger : colors.primary 
                 }}></i>
              <span>{message}</span>
            </div>
            <button
              onClick={() => setMessage('')}
              style={{
                background: 'none',
                border: 'none',
                color: colors.textLight,
                cursor: 'pointer'
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Tasks Table */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '15px',
          border: `1px solid ${colors.borderDark}`,
          overflow: 'hidden'
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            color: 'white',
            padding: '15px 20px',
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fas fa-table"></i>
              Unassigned Tasks Table
            </div>
            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
              {tasks.length} task{tasks.length !== 1 ? 's' : ''}
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              color: 'white',
              fontSize: '0.85rem'
            }}>
              <thead>
                <tr style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderBottom: `2px solid ${colors.borderDark}`
                }}>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Video Name</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Client</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Count</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Shoot Date</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Created</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Raw Link</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Current Designer</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Assign Designer</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.length === 0 ? (
                  <tr>
                    <td colSpan="12" style={{ padding: '40px', textAlign: 'center', color: colors.textLight }}>
                      <i className="fas fa-check-circle fa-2x" style={{ marginBottom: '15px', color: colors.success, opacity: 0.7 }}></i>
                      <div>All tasks have designers assigned!</div>
                    </td>
                  </tr>
                ) : (
                  tasks.map((task) => (
                    <tr key={task.id} style={{
                      borderBottom: `1px solid ${colors.borderDark}`,
                      transition: 'background 0.3s'
                    }}>
                      <td style={{ padding: '12px 15px', fontWeight: '600' }}>
                        #{task.id}
                      </td>
                      <td style={{ padding: '12px 15px', fontWeight: '500' }}>
                        {task.video_name}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {getVideoTypeBadge(task.video_type)}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.client_name || 'N/A'}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {getStatusBadge(task.status)}
                      </td>
                      <td style={{ padding: '12px 15px', textAlign: 'center', fontWeight: '600' }}>
                        {task.video_count || 1}
                      </td>
                      <td style={{ padding: '12px 15px', color: colors.textLight }}>
                        {formatDate(task.shoot_date)}
                      </td>
                      <td style={{ padding: '12px 15px', color: colors.textLight }}>
                        {formatDate(task.created_at)}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.raw_link ? (
                          <button
                            onClick={() => openRawLink(task.raw_link)}
                            style={{
                              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <i className="fas fa-external-link-alt"></i>
                            View
                          </button>
                        ) : (
                          <span style={{ color: colors.textLight, fontSize: '0.8rem' }}>N/A</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.designer_name ? (
                          <span style={{ color: colors.success, fontWeight: '500' }}>
                            <i className="fas fa-user-check me-1"></i>
                            {task.designer_name}
                          </span>
                        ) : (
                          <span style={{ color: colors.warning, fontWeight: '500' }}>
                            <i className="fas fa-user-times me-1"></i>
                            Not Assigned
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <select
                          value={task.designer_id || ''}
                          onChange={(e) => assignDesigner(task.id, e.target.value)}
                          disabled={assigning}
                          style={{
                            background: task.designer_id 
                              ? 'rgba(16, 185, 129, 0.1)' 
                              : 'rgba(255, 255, 255, 0.1)',
                            border: `1px solid ${task.designer_id ? colors.success : colors.borderDark}`,
                            color: task.designer_id ? colors.success : 'white',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            minWidth: '150px',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">Select Designer</option>
                          {designers.map(designer => (
                            <option key={designer.id} value={designer.id}>
                              {designer.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => navigate(`/edit-editor-task/${task.id}`)}
                            style={{
                              background: `linear-gradient(135deg, ${colors.warning}, #eab308)`,
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                            title="Edit Task"
                          >
                            <i className="fas fa-edit"></i>
                            Edit
                          </button>
                          
                          <button
                            onClick={fetchUnassignedTasks}
                            style={{
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: `1px solid ${colors.borderDark}`,
                              color: colors.textLight,
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                            title="Refresh"
                          >
                            <i className="fas fa-sync"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        tr:hover {
          background: rgba(255, 255, 255, 0.03) !important;
        }
        
        select:focus {
          outline: none;
          border-color: ${colors.primary} !important;
          box-shadow: 0 0 0 2px ${colors.primary}20;
        }
        
        select option {
          background: ${colors.backgroundDark};
          color: white;
          padding: 8px;
        }
        
        select option:checked {
          background: ${colors.primary};
          color: white;
        }
      `}</style>
    </Layout>
  );
}

export default UnassignedEditorTasks;